import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Select, Loading, EmptyState } from '../components/ui';

function timeToMin(t) {
  if (!t) return 0;
  const clean = t.slice(0, 5);
  const [h, m] = clean.split(':').map(Number);
  return h * 60 + m;
}

function calcOpMinutes(form) {
  try {
    const shift = timeToMin(form.shift_end) - timeToMin(form.shift_start);
    const lunch = timeToMin(form.lunch_end) - timeToMin(form.lunch_start);
    const result = shift - lunch;
    return result > 0 ? result : 0;
  } catch { return 0; }
}

export default function ShiftLineMaster() {
  const [lines, setLines] = useState([]);
  const [factories, setFactories] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterDivision, setFilterDivision] = useState('');
  const [filterFactory, setFilterFactory] = useState('');
  const [form, setForm] = useState({
    factory_id: '', line_number: '', shift_name: 'General',
    shift_start: '08:30', lunch_start: '12:30', lunch_end: '13:15', shift_end: '17:30'
  });
  const [error, setError] = useState('');

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Shift-line master'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterFactory ? { factory_id: filterFactory } : {};
      const [l, f, d] = await Promise.all([
        api.get('/shift-lines', { params }),
        api.get('/factories'),
        api.get('/divisions')
      ]);
      setLines(l.data);
      setFactories(f.data);
      setDivisions(d.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterFactory, filterDivision]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditing(null);
    setForm({ factory_id: factories[0]?.id || '', line_number: '', shift_name: 'General', shift_start: '08:30', lunch_start: '12:30', lunch_end: '13:15', shift_end: '17:30' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({
      factory_id: l.factory_id,
      line_number: l.line_number,
      shift_name: l.shift_name,
      shift_start: l.shift_start?.slice(0, 5) || '08:30',
      lunch_start: l.lunch_start?.slice(0, 5) || '12:30',
      lunch_end: l.lunch_end?.slice(0, 5) || '13:15',
      shift_end: l.shift_end?.slice(0, 5) || '17:30'
    });
    setError(''); setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.factory_id || !form.line_number) { setError('Factory and line number are required'); return; }
    const opMin = calcOpMinutes(form);
    if (opMin <= 0) { setError('Please check shift times — operating time must be positive'); return; }
    try {
      const payload = { ...form, operating_minutes: opMin };
      if (editing) await api.put(`/shift-lines/${editing.id}`, payload);
      else await api.post('/shift-lines', payload);
      setModalOpen(false); fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const timeField = (label, key) => (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <Input value={form[key]} onChange={v => setForm(p => ({ ...p, [key]: v }))} type="time" style={{ width: '100%' }} />
    </div>
  );

  // Display operating minutes — compute from stored times or from operating_minutes column
  const displayOpMin = (l) => {
    const om = l.operating_minutes;
    if (om && parseInt(om) > 0) return parseInt(om);
    // fallback: compute from times
    return calcOpMinutes({
      shift_start: l.shift_start?.slice(0, 5),
      shift_end: l.shift_end?.slice(0, 5),
      lunch_start: l.lunch_start?.slice(0, 5),
      lunch_end: l.lunch_end?.slice(0, 5)
    });
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Shift-line master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{lines.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Production lines with shift timings</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Select value={filterDivision} onChange={v => { setFilterDivision(v); setFilterFactory(''); }} placeholder="All divisions"
            options={divisions.map(d => ({ value: d.id, label: d.name }))} />
          <Select value={filterFactory} onChange={setFilterFactory} placeholder="All units"
            options={factories.filter(f => !filterDivision || String(f.division_id) === String(filterDivision)).map(f => ({ value: f.id, label: f.name }))} />
          <Btn variant="primary" size="sm" onClick={openAdd}>+ Add new</Btn>
        </div>
      </div>

      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        {loading ? <Loading /> : lines.length === 0
          ? <EmptyState message="No lines yet" action={<Btn variant="primary" onClick={openAdd}>+ Add line</Btn>} />
          : (
            <table className="lc-table">
              <thead>
                <tr>
                  <th>Line</th><th>Shift</th><th>Factory</th>
                  <th>Start</th><th>Lunch</th><th>End</th><th>Op. time</th><th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 500 }}>Line {l.line_number}</td>
                    <td><Badge type="blue">{l.shift_name}</Badge></td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>{l.factory_name}</td>
                    <td style={{ fontSize: 11.5 }}>{l.shift_start?.slice(0, 5)}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                      {l.lunch_start?.slice(0, 5)} – {l.lunch_end?.slice(0, 5)}
                    </td>
                    <td style={{ fontSize: 11.5 }}>{l.shift_end?.slice(0, 5)}</td>
                    <td>
                      <span style={{ fontWeight: 500, color: 'var(--acc)' }}>
                        {displayOpMin(l)} min
                      </span>
                    </td>
                    <td>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(l)}>Edit</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit shift-line' : 'Add shift-line'} width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Factory</div>
            <Select value={form.factory_id} onChange={v => setForm(p => ({ ...p, factory_id: v }))}
              placeholder="Select unit…"
              options={factories.filter(f => !filterDivision || String(f.division_id) === String(filterDivision)).map(f => ({ value: f.id, label: f.name }))}
              style={{ width: '100%' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Line number</div>
              <Input value={form.line_number} onChange={v => setForm(p => ({ ...p, line_number: v }))}
                placeholder="e.g. 1" style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Shift name</div>
              <Select value={form.shift_name} onChange={v => setForm(p => ({ ...p, shift_name: v }))}
                options={['General', 'First', 'Second', 'Third'].map(s => ({ value: s, label: s }))}
                style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {timeField('Shift start', 'shift_start')}
            {timeField('Shift end', 'shift_end')}
            {timeField('Lunch start', 'lunch_start')}
            {timeField('Lunch end', 'lunch_end')}
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 7, padding: '8px 12px', fontSize: 12 }}>
            Operating time: <strong style={{ color: 'var(--acc)' }}>{calcOpMinutes(form)} minutes</strong>
            <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 11 }}>
              = (shift end − shift start) − lunch break
            </span>
          </div>
          {error && (
            <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}